import { postSignupPath } from "../../utils/postSignupPath.js";
import Input from "../../components/common/input.jsx";
import Button from "../../components/common/button.jsx";
import { useFormik } from "formik";
import Joi from "joi";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { resolveLocale } from "../../i18n";
import { publicLang, publicPath } from "../public/publicPaths";
import useAuth from "../../../context/auth.context.jsx";
import { peekPendingReferral, stashPendingReferral, clearPendingReferral } from "../../utils/referral.js";
import { toast } from "react-hot-toast";
import logo from "../../assets/logo.png";
import GoogleAuthFlow from "../../components/googleAuth.jsx";
import AppleAuthFlow from "../../components/appleAuth.jsx";
import SignupConsentFields from "../../components/signupConsent.jsx";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["auth", "common"]);

  const capturedCode = (peekPendingReferral() || "").toUpperCase();
  const { getFieldProps, handleSubmit, touched, errors, isSubmitting, setFieldValue, values } = useFormik({
    initialValues: {
      name: "",
      phone: "",
      email: "",
      password: "",
      invitedByCode: capturedCode,
      acceptedLegal: false,
    },
    validate(values) {
      const schema = Joi.object({
        name: Joi.string().required().min(2).max(50).messages({
          "string.empty": t("auth:nameRequired"),
          "string.min": t("auth:nameTooShort"),
        }),
        // Optional: App Review 5.1.1(v) does not allow requiring a phone number
        // for a feature set that works without one.
        phone: Joi.string().allow("").optional().custom((value, helpers) => {
          const digits = String(value).replace(/\D/g, "");
          if (!digits) return "";
          if (!/^[0-9]{7,15}$/.test(digits)) return helpers.error("string.pattern.base");
          return digits;
        }).messages({
          "string.pattern.base": t("auth:phoneInvalid"),
        }),
        email: Joi.string().required().email({ tlds: false }).messages({
          "string.email": t("auth:emailInvalid"),
          "string.empty": t("auth:emailRequired"),
        }),
        password: Joi.string()
          .pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])[A-Za-z\d@$!%*?&]{8,}$/)
          .required()
          .messages({
            "string.pattern.base": t("auth:passwordPattern"),
            "string.empty": t("auth:passwordRequired"),
          }),
        invitedByCode: Joi.string().allow("").max(6).pattern(/^[A-Za-z0-9]{0,6}$/).messages({
          "string.max": t("auth:referralCodeInvalid"),
          "string.pattern.base": t("auth:referralCodeInvalid"),
        }),
        acceptedLegal: Joi.boolean().valid(true).required().messages({
          "any.only": t("auth:consentRequired"),
          "any.required": t("auth:consentRequired"),
        }),
      });
      const { error } = schema.validate(values, { abortEarly: false });
      if (!error) return null;
      const errs = {};
      for (let item of error.details) errs[item.path[0]] = item.message;
      return errs;
    },
    onSubmit: async (values) => {
      try {
        const locale = resolveLocale(i18n.resolvedLanguage || i18n.language);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const invitedByCode = (values.invitedByCode || peekPendingReferral() || "").trim().toUpperCase();
        const phone = values.phone ? String(values.phone).replace(/\D/g, "") : "";
        await register({ ...values, phone, locale, timezone, invitedByCode: invitedByCode || undefined });
        clearPendingReferral(); // only after a successful registration
        navigate(postSignupPath());
      } catch (error) {
        if (error.response?.status === 409) {
          toast.error(error.response?.data?.error || t("auth:emailOrPhoneTaken"));
        } else {
          toast.error(error.response?.data?.error || t("auth:somethingWentWrong"));
        }
      }
    },
  });

  const signupConsent = {
    acceptedLegal: values.acceptedLegal,
  };

  return (
    <div className="auth-page">
      <div className="text-center mb-4">
        <img src={logo} alt="Master Splitter" width="72" height="72" style={{ borderRadius: 18 }} />
        <h1 className="fs-3 fw-bold mt-3 mb-1">{t("auth:createAccount")}</h1>
        <p className="text-muted-ms">{t("auth:createAccountHint")}</p>
      </div>

      <form onSubmit={handleSubmit} className="ms-card">
        <Input {...getFieldProps("name")} name="name" label={t("auth:fullName")} type="text" autoComplete="name"
          error={touched.name ? errors.name : ""} placeholder={t("auth:namePlaceholder")} required />
        <Input {...getFieldProps("phone")} name="phone" label={t("auth:phoneOptional")} type="tel" autoComplete="tel" inputMode="numeric"
          error={touched.phone ? errors.phone : ""} />
        <Input {...getFieldProps("email")} name="email" label={t("auth:email")} type="email" autoComplete="email"
          error={touched.email ? errors.email : ""} placeholder="name@domain.com" required />
        <Input {...getFieldProps("password")} name="password" label={t("auth:password")} type="password" autoComplete="new-password"
          error={touched.password ? errors.password : ""} required />
        <Input
          {...getFieldProps("invitedByCode")}
          name="invitedByCode"
          label={t("auth:referralCode")}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          maxLength={6}
          dir="ltr"
          placeholder="ABC123"
          error={touched.invitedByCode ? errors.invitedByCode : ""}
          onChange={(e) => {
            const next = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6).toUpperCase();
            setFieldValue("invitedByCode", next);
            if (next) stashPendingReferral(next);
            else clearPendingReferral();
          }}
        />
        <p className="text-muted-ms" style={{ fontSize: "0.85rem", marginTop: "-0.5rem", marginBottom: "1rem" }}>
          {values.invitedByCode && capturedCode && values.invitedByCode === capturedCode
            ? t("auth:referralCodeCaptured")
            : t("auth:referralCodeHint")}
        </p>
        <SignupConsentFields
          acceptedLegalProps={getFieldProps({ name: "acceptedLegal", type: "checkbox" })}
          acceptedLegalError={touched.acceptedLegal ? errors.acceptedLegal : ""}
        />
        <Button type="submit" text={t("auth:register")} disabled={isSubmitting} />
        <button
          type="button"
          className="btn-ms btn-ms-outline btn-ms-block mt-2"
          onClick={() => navigate("/login")}
        >
          {t("auth:haveAccountLogin")}
        </button>
        <GoogleAuthFlow signupConsent={signupConsent} />
        <AppleAuthFlow signupConsent={signupConsent} />
      </form>
      <nav className="auth-legal-links" aria-label={t("common:moreInfo")}>
        <Link to={publicPath(publicLang(i18n.language), '/about')}>{t("auth:whatIsMasterSplitter")}</Link>
      </nav>
    </div>
  );
}
